export type HomeSocialConversionDay = {
  missionStarted: number;
  missionCompleted: number;
  rankedSearches: number;
  rankedSearchResults: number;
  followsFromSearch: number;
  followsFromSuggestion: number;
  qualityModeBalanced: number;
  qualityModeStrict: number;
  weeklyRecapViewed: number;
  assistImpressionsMinimal: number;
  assistImpressionsRicher: number;
  assistActionsMinimal: number;
  assistActionsRicher: number;
  assistFollowThroughMinimal: number;
  assistFollowThroughRicher: number;
};

type HomeSocialConversionTelemetry = {
  byDay: Record<string, HomeSocialConversionDay>;
  updatedAt: string;
};

type ConversionEvent =
  | { kind: "mission_started" }
  | { kind: "mission_completed" }
  | { kind: "ranked_search"; resultCount: number }
  | { kind: "follow_search" }
  | { kind: "follow_suggestion" }
  | { kind: "quality_balanced" }
  | { kind: "quality_strict" }
  | { kind: "weekly_recap_viewed" }
  | { kind: "assist_impression"; variant: "minimal" | "richer" }
  | { kind: "assist_action"; variant: "minimal" | "richer" }
  | { kind: "assist_follow_through"; variant: "minimal" | "richer" };

const STORAGE_KEY = "tension_home_social_conversion_v1";
const MAX_DAYS = 28;

function todayIsoKey(): string {
  return new Date().toISOString().slice(0, 10);
}

function defaultDay(): HomeSocialConversionDay {
  return {
    missionStarted: 0,
    missionCompleted: 0,
    rankedSearches: 0,
    rankedSearchResults: 0,
    followsFromSearch: 0,
    followsFromSuggestion: 0,
    qualityModeBalanced: 0,
    qualityModeStrict: 0,
    weeklyRecapViewed: 0,
    assistImpressionsMinimal: 0,
    assistImpressionsRicher: 0,
    assistActionsMinimal: 0,
    assistActionsRicher: 0,
    assistFollowThroughMinimal: 0,
    assistFollowThroughRicher: 0,
  };
}

export function readHomeSocialConversionTelemetry(): HomeSocialConversionTelemetry {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (!parsed || typeof parsed !== "object") {
      return { byDay: {}, updatedAt: new Date().toISOString() };
    }
    return {
      byDay: parsed.byDay && typeof parsed.byDay === "object" ? parsed.byDay : {},
      updatedAt: typeof parsed.updatedAt === "string" ? parsed.updatedAt : new Date().toISOString(),
    };
  } catch {
    return { byDay: {}, updatedAt: new Date().toISOString() };
  }
}

function writeHomeSocialConversionTelemetry(payload: HomeSocialConversionTelemetry): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // noop
  }
}

export function recordHomeSocialConversionEvent(event: ConversionEvent): void {
  const telemetry = readHomeSocialConversionTelemetry();
  const key = todayIsoKey();
  const day = telemetry.byDay[key] ?? defaultDay();
  const next = { ...day };
  switch (event.kind) {
    case "mission_started":
      next.missionStarted += 1;
      break;
    case "mission_completed":
      next.missionCompleted += 1;
      break;
    case "ranked_search":
      next.rankedSearches += 1;
      next.rankedSearchResults += Math.max(0, Math.floor(Number(event.resultCount || 0)));
      break;
    case "follow_search":
      next.followsFromSearch += 1;
      break;
    case "follow_suggestion":
      next.followsFromSuggestion += 1;
      break;
    case "quality_balanced":
      next.qualityModeBalanced += 1;
      break;
    case "quality_strict":
      next.qualityModeStrict += 1;
      break;
    case "weekly_recap_viewed":
      next.weeklyRecapViewed += 1;
      break;
    case "assist_impression":
      if (event.variant === "minimal") next.assistImpressionsMinimal += 1;
      else next.assistImpressionsRicher += 1;
      break;
    case "assist_action":
      if (event.variant === "minimal") next.assistActionsMinimal += 1;
      else next.assistActionsRicher += 1;
      break;
    case "assist_follow_through":
      if (event.variant === "minimal") next.assistFollowThroughMinimal += 1;
      else next.assistFollowThroughRicher += 1;
      break;
  }
  telemetry.byDay[key] = next;
  const keys = Object.keys(telemetry.byDay).sort();
  if (keys.length > MAX_DAYS) {
    const toDrop = keys.slice(0, keys.length - MAX_DAYS);
    toDrop.forEach((dropKey) => delete telemetry.byDay[dropKey]);
  }
  telemetry.updatedAt = new Date().toISOString();
  writeHomeSocialConversionTelemetry(telemetry);
}

